import "./index.css";
// src/Home/Home.tsx
import ContentLayout from "@cloudscape-design/components/content-layout";
import Header2 from "@cloudscape-design/components/header";
import Icon2 from "@cloudscape-design/components/icon";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Alert from "@cloudscape-design/components/alert";
import * as React2 from "react";
import { useAppTranslation as useAppTranslation2 } from "@amzn/awsmp-max-translations";
import { ForceDeleteModal } from "@amzn/awsmp-max-ux-patterns/ForceDeleteModal";
import { ContentGrid } from "@amzn/awsmp-max-ux-patterns/ContentGrid";
import { NewProjectSection } from "@amzn/awsmp-max-ux-patterns/NewProjectSection";

// src/Home/Home.module.css
var Home_default = {
  headerLayout: "Home_headerLayout",
  headerContent: "Home_headerContent",
  genAiIcon: "Home_genAiIcon"
};

// src/Home/ProjectsTableSection.tsx
import Button from "@cloudscape-design/components/button";
import Header from "@cloudscape-design/components/header";
import Icon from "@cloudscape-design/components/icon";
import Link from "@cloudscape-design/components/link";
import Tabs from "@cloudscape-design/components/tabs";
import * as React from "react";
import { ManagedTable } from "@amzn/awsmp-max-ux-patterns/ManagedTable";
import { useAppTranslation } from "@amzn/awsmp-max-translations";

// src/Home/ProjectsTableSection.module.css
var ProjectsTableSection_default = {
  favoriteIcon: "ProjectsTableSection_favoriteIcon",
  favoriteIconDisabled: "ProjectsTableSection_favoriteIconDisabled"
};

// src/Home/ProjectsTableSection.tsx
import { jsx } from "react/jsx-runtime";
var formatDate = (date, t) => {
  const now = /* @__PURE__ */ new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffMinutes = Math.floor(diffTime / (1e3 * 60));
  const diffHours = Math.floor(diffTime / (1e3 * 60 * 60));
  const diffDays = Math.ceil(diffTime / (1e3 * 60 * 60 * 24));
  if (diffMinutes < 1)
    return t("justNow");
  if (diffMinutes === 1)
    return t("minuteAgo");
  if (diffMinutes < 60)
    return t("minutesAgo", { count: diffMinutes });
  if (diffHours === 1)
    return t("hourAgo");
  if (diffHours < 24)
    return t("hoursAgo", { count: diffHours });
  if (diffDays === 1)
    return t("dayAgo");
  if (diffDays < 7)
    return t("daysAgo", { count: diffDays });
  if (diffDays < 14)
    return t("weekAgo");
  if (diffDays < 30)
    return t("weeksAgo", { count: Math.ceil(diffDays / 7) });
  return date.toLocaleDateString();
};
var ProjectsTableSection = ({
  projects,
  selectedItems,
  onSelectionChange,
  onProjectClick,
  onToggleFavorite,
  updatingFavorites = /* @__PURE__ */ new Set(),
  onDeleteProjects,
  isLoadingProjects,
  isLoadingMore,
  isDeletingProjects,
  logger
}) => {
  const { t } = useAppTranslation();
  const [activeTabId, setActiveTabId] = React.useState("all-projects");
  const createDeleteButton = () => {
    const isDisabled = selectedItems.length === 0 || isDeletingProjects || isLoadingMore;
    let ariaLabel = "";
    if (selectedItems.length === 0) {
      ariaLabel = t("deleteProjects") + " (" + t("noProjectsSelected") + ")";
    } else if (isLoadingMore) {
      ariaLabel = t("deleteProjects") + " (" + t("loadingMoreProjects") + ")";
    } else {
      ariaLabel = t("deleteSelectedProjects", { count: selectedItems.length });
    }
    return /* @__PURE__ */ jsx(
      Button,
      {
        disabled: isDisabled,
        onClick: () => {
          logger?.biMetric({
            metricName: "Click",
            value: 1,
            unit: "Count",
            dimensions: {
              ClickObj: "home-projects-table",
              ClickSelect: "delete-projects-button"
            }
          });
          onDeleteProjects();
        },
        loading: isDeletingProjects,
        ariaLabel,
        children: t("delete")
      }
    );
  };
  const tableColumns = [
    {
      id: "name",
      header: t("name"),
      cell: (item) => /* @__PURE__ */ jsx(
        Link,
        {
          onFollow: () => {
            logger?.biMetric({
              metricName: "Click",
              value: 1,
              unit: "Count",
              dimensions: {
                ClickObj: "home-projects-table",
                ClickSelect: "project-name-link",
                ClickAct: item.id
              }
            });
            onProjectClick(item);
          },
          variant: "primary",
          children: item.name
        }
      ),
      sortingField: "name"
    },
    {
      id: "lastModified",
      header: t("lastModified"),
      cell: (item) => formatDate(item.lastModified, t),
      sortingField: "lastModified"
    },
    {
      id: "favorite",
      header: t("favorite"),
      cell: (item) => {
        const isUpdating = updatingFavorites.has(item.id);
        return /* @__PURE__ */ jsx(
          "div",
          {
            onClick: () => !isUpdating && onToggleFavorite(item.id),
            className: `${ProjectsTableSection_default.favoriteIcon} ${isUpdating ? ProjectsTableSection_default.favoriteIconDisabled : ""}`,
            role: "button",
            tabIndex: isUpdating ? -1 : 0,
            onKeyDown: (e) => {
              if (!isUpdating && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onToggleFavorite(item.id);
              }
            },
            "aria-label": item.isFavorite ? t("removeFromFavorites") : t("addToFavorites"),
            "aria-disabled": isUpdating,
            children: /* @__PURE__ */ jsx(Icon, { name: item.isFavorite ? "star-filled" : "star" })
          }
        );
      },
      width: 100,
      minWidth: 100
    }
  ];
  const favoriteProjects = projects.filter((project) => project.isFavorite);
  return /* @__PURE__ */ jsx(
    Tabs,
    {
      activeTabId,
      onChange: ({ detail }) => setActiveTabId(detail.activeTabId),
      tabs: [
        {
          id: "all-projects",
          label: t("allProjects"),
          content: /* @__PURE__ */ jsx(
            ManagedTable,
            {
              preferenceKey: "projects-table",
              columnDefinitions: tableColumns,
              items: projects,
              selectedItems,
              onSelectionChange: ({ detail }) => onSelectionChange(detail.selectedItems),
              selectionType: "multi",
              loading: isLoadingProjects,
              ariaLabels: {
                selectionGroupLabel: t("itemsSelection"),
                allItemsSelectionLabel: ({ selectedItems: selectedItems2 }) => t("itemsSelected", {
                  count: selectedItems2.length,
                  plural: selectedItems2.length === 1 ? t("item") : t("items")
                }),
                itemSelectionLabel: ({ selectedItems: selectedItems2 }, item) => {
                  const isItemSelected = selectedItems2.filter((i) => i.id === item.id).length;
                  return isItemSelected ? t("isSelected", { name: item.name }) : t("isNotSelected", { name: item.name });
                }
              },
              trackBy: "id",
              filtering: {
                filteringPlaceholder: t("searchProjects"),
                filteringAriaLabel: t("filterProjects")
              },
              pagination: {},
              hidePreferences: true,
              header: /* @__PURE__ */ jsx(
                Header,
                {
                  counter: selectedItems.length > 0 ? `(${selectedItems.length}/${projects.length})` : `(${projects.length})`,
                  actions: createDeleteButton(),
                  children: t("allProjects")
                }
              )
            }
          )
        },
        {
          id: "favorites",
          label: t("favorites"),
          content: /* @__PURE__ */ jsx(
            ManagedTable,
            {
              preferenceKey: "projects-table",
              columnDefinitions: tableColumns,
              items: favoriteProjects,
              selectedItems: selectedItems.filter((item) => item.isFavorite),
              onSelectionChange: ({ detail }) => onSelectionChange(detail.selectedItems),
              selectionType: "multi",
              loading: isLoadingProjects,
              ariaLabels: {
                selectionGroupLabel: t("itemsSelection"),
                allItemsSelectionLabel: ({ selectedItems: selectedItems2 }) => t("itemsSelected", {
                  count: selectedItems2.length,
                  plural: selectedItems2.length === 1 ? t("item") : t("items")
                }),
                itemSelectionLabel: ({ selectedItems: selectedItems2 }, item) => {
                  const isItemSelected = selectedItems2.filter((i) => i.id === item.id).length;
                  return isItemSelected ? t("isSelected", { name: item.name }) : t("isNotSelected", { name: item.name });
                }
              },
              trackBy: "id",
              filtering: {
                filteringPlaceholder: t("searchFavoriteProjects"),
                filteringAriaLabel: t("filterFavoriteProjects")
              },
              pagination: {},
              hidePreferences: true,
              header: /* @__PURE__ */ jsx(
                Header,
                {
                  counter: selectedItems.filter((item) => item.isFavorite).length > 0 ? `(${selectedItems.filter((item) => item.isFavorite).length}/${favoriteProjects.length})` : `(${favoriteProjects.length})`,
                  actions: createDeleteButton(),
                  children: t("favoriteProjects")
                }
              )
            }
          )
        }
      ]
    }
  );
};

// src/Home/Home.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var transformProjectsToItems = (projects) => {
  return projects.map((project) => ({
    id: project.projectId,
    name: project.name,
    lastModified: new Date(project.endTimestamp),
    // Use endTimestamp (last modified time)
    isFavorite: project.tags?.includes("favourite") ?? false
  }));
};
var Home = ({ maxService, projectCreation, navigation, logger, layout, queryParamProject }) => {
  const { listProjects, deleteProject, updateProjectTags } = maxService;
  const { navigate } = navigation;
  const { modal, flashbar } = layout;
  const { t } = useAppTranslation2();
  const [projects, setProjects] = React2.useState([]);
  const [selectedItems, setSelectedItems] = React2.useState([]);
  const [localError, setLocalError] = React2.useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = React2.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React2.useState(false);
  const [isDeletingProjects, setIsDeletingProjects] = React2.useState(false);
  const [updatingFavorites, setUpdatingFavorites] = React2.useState(/* @__PURE__ */ new Set());
  const { error: queryParamError } = queryParamProject;
  const shownQueryErrorRef = React2.useRef(null);
  React2.useEffect(() => {
    if (queryParamError && queryParamError !== shownQueryErrorRef.current) {
      shownQueryErrorRef.current = queryParamError;
      logger.error(`Query parameter error: ${queryParamError}`);
      flashbar.addItem({
        type: "error",
        header: t("failedToCreateProjectFromUrl"),
        content: queryParamError,
        dismissible: true
      });
    } else if (!queryParamError) {
      shownQueryErrorRef.current = null;
    }
  }, [queryParamError, flashbar, logger]);
  React2.useEffect(() => {
    return () => {
      flashbar.clearAll();
    };
  }, []);
  const displayError = localError;
  const loadProjects = React2.useCallback(async () => {
    const startTime = performance.now();
    let firstPageTime = null;
    setLocalError(null);
    setIsLoadingProjects(true);
    setProjects([]);
    let isFirstPage = true;
    try {
      await listProjects({
        onPageLoaded: (pageProjects) => {
          const transformedProjects = transformProjectsToItems(pageProjects);
          setProjects((prev) => [...prev, ...transformedProjects]);
          if (isFirstPage) {
            firstPageTime = performance.now();
            const firstPageDuration = firstPageTime - startTime;
            logger.opsMetric({
              metricName: "ProjectsFirstPageLoaded",
              value: firstPageDuration,
              unit: "Milliseconds"
            });
            setIsLoadingProjects(false);
            setIsLoadingMore(true);
            isFirstPage = false;
          }
        }
      });
      const totalDuration = performance.now() - startTime;
      logger.opsMetric({
        metricName: "ProjectsFullyLoaded",
        value: totalDuration,
        unit: "Milliseconds"
      });
      setIsLoadingMore(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("failedToLoadProjects");
      logger.error(`Failed to load projects: ${errorMessage}`);
      setLocalError(t("failedToLoadProjects"));
      setProjects([]);
      setIsLoadingProjects(false);
      setIsLoadingMore(false);
    }
  }, [listProjects, logger]);
  React2.useEffect(() => {
    loadProjects();
  }, [loadProjects]);
  const toggleFavorite = React2.useCallback(
    async (projectId) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project)
        return;
      if (updatingFavorites.has(projectId))
        return;
      const newIsFavorite = !project.isFavorite;
      const newTags = newIsFavorite ? ["favourite"] : [];
      setProjects((prevProjects) => prevProjects.map((p) => p.id === projectId ? { ...p, isFavorite: newIsFavorite } : p));
      setUpdatingFavorites((prev) => new Set(prev).add(projectId));
      try {
        await updateProjectTags(projectId, newTags);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t("unknownError");
        logger.error(`Failed to update favorite status for project ${projectId}: ${errorMessage}`);
        setProjects((prevProjects) => prevProjects.map((p) => p.id === projectId ? { ...p, isFavorite: !newIsFavorite } : p));
      } finally {
        setUpdatingFavorites((prev) => {
          const next = new Set(prev);
          next.delete(projectId);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, updateProjectTags, updatingFavorites, logger]
  );
  const handleProjectClick = React2.useCallback(
    (project) => {
      navigate("project", { projectId: project.id });
    },
    [navigate]
  );
  const handleDeleteSelected = React2.useCallback(async () => {
    if (selectedItems.length === 0)
      return;
    setIsDeletingProjects(true);
    try {
      const results = await Promise.all(
        selectedItems.map(
          (item) => deleteProject(item.id, {
            onForceDeleteRequired: (performForceDelete) => {
              setIsDeletingProjects(false);
              modal.updateMetadata({ size: "medium" });
              modal.setContent({
                header: t("deleteActiveProjects"),
                content: /* @__PURE__ */ jsx2(
                  ForceDeleteModal,
                  {
                    projects: selectedItems.map((i) => ({ id: i.id, name: i.name })),
                    onConfirm: async () => {
                      await performForceDelete();
                      modal.hide();
                      setSelectedItems([]);
                      await loadProjects();
                      const firstName = selectedItems[0].name;
                      const content = selectedItems.length === 1 ? `Successfully deleted ${firstName}` : `Successfully deleted ${firstName} and ${selectedItems.length - 1} more`;
                      flashbar.addItem({
                        type: "success",
                        header: t("projectsDeleted", { count: selectedItems.length }),
                        content,
                        dismissible: true
                      });
                    },
                    onCancel: () => modal.hide()
                  }
                )
              });
            }
          })
        )
      );
      const allSucceeded = results.every((result) => result.success !== false);
      if (allSucceeded) {
        setSelectedItems([]);
        await loadProjects();
        const firstName = selectedItems[0].name;
        const content = selectedItems.length === 1 ? `Successfully deleted ${firstName}` : `Successfully deleted ${firstName} and ${selectedItems.length - 1} more`;
        flashbar.addItem({
          type: "success",
          header: t("projectsDeleted", { count: selectedItems.length }),
          content,
          dismissible: true
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("failedToDeleteProjects");
      flashbar.addItem({
        type: "error",
        header: t("failedToDeleteProjects"),
        content: errorMessage,
        dismissible: true
      });
    } finally {
      setIsDeletingProjects(false);
    }
  }, [deleteProject, selectedItems, loadProjects, modal, flashbar]);
  return /* @__PURE__ */ jsx2(
    ContentLayout,
    {
      header: /* @__PURE__ */ jsx2("div", { className: Home_default.headerLayout, children: /* @__PURE__ */ jsx2("div", { className: Home_default.headerContent, children: /* @__PURE__ */ jsxs(SpaceBetween, { direction: "horizontal", size: "xs", alignItems: "center", children: [
        /* @__PURE__ */ jsx2(Icon2, { name: "gen-ai", className: Home_default.genAiIcon }),
        /* @__PURE__ */ jsx2(Header2, { variant: "h1", children: t("aiMode") })
      ] }) }) }),
      children: /* @__PURE__ */ jsx2(ContentGrid, { children: /* @__PURE__ */ jsxs(SpaceBetween, { size: "l", children: [
        displayError && /* @__PURE__ */ jsxs(Alert, { type: "error", dismissible: true, onDismiss: () => setLocalError(null), children: [
          t("error"),
          ": ",
          displayError
        ] }),
        /* @__PURE__ */ jsx2(
          NewProjectSection,
          {
            projectCreation,
            navigation,
            flashbar,
            variant: "full",
            placeholderText: t("tellUsAboutYourBusinessNeed"),
            logger
          }
        ),
        /* @__PURE__ */ jsx2(
          ProjectsTableSection,
          {
            projects,
            selectedItems,
            onSelectionChange: setSelectedItems,
            onProjectClick: handleProjectClick,
            onToggleFavorite: toggleFavorite,
            updatingFavorites,
            onDeleteProjects: handleDeleteSelected,
            isLoadingProjects,
            isLoadingMore,
            isDeletingProjects,
            logger
          }
        )
      ] }) })
    }
  );
};

// src/Project/Project.tsx
import ContentLayout2 from "@cloudscape-design/components/content-layout";
import SpaceBetween2 from "@cloudscape-design/components/space-between";
import Box from "@cloudscape-design/components/box";
import Spinner from "@cloudscape-design/components/spinner";
import Button2 from "@cloudscape-design/components/button";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import { colorBackgroundStatusInfo } from "@cloudscape-design/design-tokens";
import * as React3 from "react";
import { TurnStatus } from "@amzn/awsmp-max-model";
import {
  Message,
  Text,
  AskUser,
  WebSearch,
  AnalysisSteps,
  InlineThink,
  PricingAnnotation,
  ProvideRequirementsAnnotation,
  LinkToArtifact,
  createSplitPanelArtifactComponents,
  NotificationBlock,
  SourcesList
} from "@amzn/awsmp-max-agentic-ui-messaging";
import { PromptInput } from "@amzn/awsmp-max-ux-patterns/PromptInput";
import { ProductComparison, ProductSearch, ProductEvaluation, ArtifactFallback } from "@amzn/awsmp-max-agentic-ui-artifacts";
import { useAppTranslation as useAppTranslation3 } from "@amzn/awsmp-max-translations";
import { ArtifactThumbnailContainer } from "@amzn/awsmp-max-ux-patterns/ArtifactThumbnailContainer";
import { Carousel } from "@amzn/awsmp-max-ux-patterns/Carousel";
import { ForceDeleteModal as ForceDeleteModal2 } from "@amzn/awsmp-max-ux-patterns/ForceDeleteModal";
import { StickyHeader } from "@amzn/awsmp-max-ux-patterns/StickyHeader";
import { ContentGrid as ContentGrid2 } from "@amzn/awsmp-max-ux-patterns/ContentGrid";

// src/Project/AutoScrollToAnchor.tsx
import { useCallback as useCallback2, useEffect as useEffect2, useRef as useRef2, forwardRef, useImperativeHandle } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var AutoScrollToAnchor = forwardRef(
  ({ children, isActive, getViewportOffsets, onAnchorVisibilityChange }, ref) => {
    const anchorRef = useRef2(null);
    const shouldAutoScroll = useRef2(true);
    const isProgrammaticScroll = useRef2(false);
    const isUserScrolling = useRef2(false);
    const lastProgrammaticScrollTime = useRef2(0);
    const scrollToBottomInProgress = useRef2(false);
    const hasCompletedInitialScroll = useRef2(false);
    const isInitialMount = useRef2(true);
    const isActivelyScrolling = useRef2(false);
    const scrollStopTimer = useRef2(null);
    const checkIfAtBottom = useCallback2(() => {
      if (!anchorRef.current)
        return false;
      const offsets = getViewportOffsets?.() ?? { top: 0, bottom: 0 };
      const rect = anchorRef.current.getBoundingClientRect();
      const effectiveViewportBottom = window.innerHeight - offsets.bottom;
      const atBottom = rect.bottom <= effectiveViewportBottom + 10;
      return atBottom;
    }, [getViewportOffsets]);
    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom: () => {
          shouldAutoScroll.current = true;
          isUserScrolling.current = false;
          onAnchorVisibilityChange?.({ anchorInView: true, isActivelyScrolling: false });
          isProgrammaticScroll.current = true;
          scrollToBottomInProgress.current = true;
          lastProgrammaticScrollTime.current = Date.now();
          if (!anchorRef.current)
            return;
          const offsets = getViewportOffsets?.() ?? { top: 0, bottom: 0 };
          const rect = anchorRef.current.getBoundingClientRect();
          const currentScrollY = window.scrollY;
          const anchorBottomAbsolute = rect.bottom + currentScrollY;
          const effectiveViewportHeight = window.innerHeight - offsets.top - offsets.bottom;
          const targetScroll = anchorBottomAbsolute - effectiveViewportHeight;
          window.scrollTo({ top: targetScroll, behavior: "smooth" });
          setTimeout(() => {
            isProgrammaticScroll.current = false;
            scrollToBottomInProgress.current = false;
            const atBottom = checkIfAtBottom();
            if (atBottom) {
              onAnchorVisibilityChange?.({ anchorInView: true, isActivelyScrolling: false });
            }
          }, 1500);
        }
      }),
      [getViewportOffsets, onAnchorVisibilityChange, checkIfAtBottom]
    );
    const scrollToAnchor = useCallback2(
      (behavior = "smooth") => {
        if (!shouldAutoScroll.current || !anchorRef.current) {
          return;
        }
        const offsets = getViewportOffsets?.() ?? { top: 0, bottom: 0 };
        const rect = anchorRef.current.getBoundingClientRect();
        const currentScrollY = window.scrollY;
        const anchorBottomAbsolute = rect.bottom + currentScrollY;
        const effectiveViewportHeight = window.innerHeight - offsets.top - offsets.bottom;
        const targetScroll = anchorBottomAbsolute - effectiveViewportHeight;
        const scrollDelta = targetScroll - currentScrollY;
        if (scrollDelta <= 0)
          return;
        const MIN_SCROLL_THRESHOLD = 10;
        if (scrollDelta < MIN_SCROLL_THRESHOLD)
          return;
        isProgrammaticScroll.current = true;
        lastProgrammaticScrollTime.current = Date.now();
        if (targetScroll > currentScrollY) {
          window.scrollTo({ top: targetScroll, behavior });
        }
        setTimeout(() => {
          isProgrammaticScroll.current = false;
          hasCompletedInitialScroll.current = true;
        }, 500);
      },
      [getViewportOffsets]
    );
    useEffect2(() => {
      if (!isActive || !anchorRef.current)
        return;
      const shouldDoInitialScroll = !isInitialMount.current;
      const initialScrollTimer = shouldDoInitialScroll ? setTimeout(() => {
        scrollToAnchor();
      }, 10) : void 0;
      let debounceTimer = null;
      const debouncedScroll = () => {
        if (debounceTimer)
          clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          scrollToAnchor();
        }, 50);
      };
      const observer = new MutationObserver(() => debouncedScroll());
      observer.observe(anchorRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        // Detect attribute changes like style.height
        attributeFilter: ["style", "class"]
        // Only watch style and class attributes
      });
      return () => {
        if (initialScrollTimer)
          clearTimeout(initialScrollTimer);
        if (debounceTimer)
          clearTimeout(debounceTimer);
        isInitialMount.current = false;
        setTimeout(() => {
          observer.disconnect();
        }, 500);
      };
    }, [isActive, scrollToAnchor]);
    useEffect2(() => {
      const timer = setTimeout(() => {
        isInitialMount.current = false;
      }, 200);
      return () => clearTimeout(timer);
    }, []);
    useEffect2(() => {
      const handleUserGesture = (e) => {
        if (e.type === "click")
          return;
        if (isProgrammaticScroll.current)
          return;
        isUserScrolling.current = true;
        if (isActive && !isInitialMount.current && shouldAutoScroll.current) {
          shouldAutoScroll.current = false;
        }
      };
      const handleScroll = () => {
        if (isProgrammaticScroll.current)
          return;
        if (scrollToBottomInProgress.current)
          return;
        isActivelyScrolling.current = true;
        if (scrollStopTimer.current) {
          clearTimeout(scrollStopTimer.current);
        }
        scrollStopTimer.current = setTimeout(() => {
          isActivelyScrolling.current = false;
          const atBottom2 = checkIfAtBottom();
          if (atBottom2) {
            shouldAutoScroll.current = true;
          }
          onAnchorVisibilityChange?.({ anchorInView: atBottom2, isActivelyScrolling: false });
        }, 500);
        const atBottom = checkIfAtBottom();
        if (!atBottom && isActive && shouldAutoScroll.current && isUserScrolling.current) {
          shouldAutoScroll.current = false;
        }
      };
      const handleKeyDown = (e) => {
        if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) {
          handleUserGesture(e);
        }
      };
      window.addEventListener("wheel", handleUserGesture, { passive: true });
      window.addEventListener("touchmove", handleUserGesture, { passive: true });
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("wheel", handleUserGesture);
        window.removeEventListener("touchmove", handleUserGesture);
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("scroll", handleScroll);
        if (scrollStopTimer.current) {
          clearTimeout(scrollStopTimer.current);
        }
      };
    }, [checkIfAtBottom, onAnchorVisibilityChange, isActive]);
    useEffect2(() => {
      if (!onAnchorVisibilityChange)
        return;
      const timer = setTimeout(() => {
        if (scrollToBottomInProgress.current)
          return;
        const atBottom = checkIfAtBottom();
        if (!atBottom) {
          onAnchorVisibilityChange({ anchorInView: false, isActivelyScrolling: false });
        }
      }, 100);
      return () => clearTimeout(timer);
    }, [checkIfAtBottom, onAnchorVisibilityChange]);
    useEffect2(() => {
      if (isActive) {
        shouldAutoScroll.current = true;
        onAnchorVisibilityChange?.({ anchorInView: true, isActivelyScrolling: false });
      }
    }, [isActive]);
    return /* @__PURE__ */ jsx3("div", { ref: anchorRef, children });
  }
);
AutoScrollToAnchor.displayName = "AutoScrollToAnchor";

// src/Project/Project.module.scss
var Project_module_default = {
  fadeInSlideUp: "Project_module_fadeInSlideUp",
  fadeOutSlideDown: "Project_module_fadeOutSlideDown",
  pageWrapper: "Project_module_pageWrapper",
  messagesContainer: "Project_module_messagesContainer",
  constrainedContent: "Project_module_constrainedContent",
  nextStepsContainer: "Project_module_nextStepsContainer",
  nextStepsHidden: "Project_module_nextStepsHidden",
  nextStepsLayout: "Project_module_nextStepsLayout",
  nextStepsLabel: "Project_module_nextStepsLabel",
  nextStepsCarousel: "Project_module_nextStepsCarousel",
  tailButtonContainer: "Project_module_tailButtonContainer",
  fadeInTailButton: "Project_module_fadeInTailButton",
  stickyInputGradient: "Project_module_stickyInputGradient",
  stickyInput: "Project_module_stickyInput",
  stickyInputContent: "Project_module_stickyInputContent"
};

// src/Project/Project.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var SourcesListWrapper = ({ data }) => /* @__PURE__ */ jsx4(SourcesList, { citations: data.citations });
var MESSAGE_COMPONENTS = {
  message: Message,
  text: Text,
  analysissteps: AnalysisSteps,
  inlinethink: InlineThink,
  pricingannotation: PricingAnnotation,
  providerequirementsannotation: ProvideRequirementsAnnotation,
  askuser: AskUser,
  // Unified component (renders on toolInput with both input/output data)
  websearch: WebSearch,
  linktoartifact: LinkToArtifact,
  notification: NotificationBlock,
  // Unified component for interrupts and errors
  sourceslist: SourcesListWrapper
};
var THUMBNAIL_ARTIFACTS = {
  search: React3.memo(({ data, navigationManager, projectId, artifactType, ...rest }) => {
    const { t } = useAppTranslation3();
    return /* @__PURE__ */ jsx4(
      ArtifactThumbnailContainer,
      {
        title: t("recommendedProducts"),
        subtitle: `${data.recommendations?.length || 0} products found`,
        onClick: () => navigationManager.navigate("artifact", { projectId, artifactType }),
        artifactContent: /* @__PURE__ */ jsx4(ProductSearch, { data, navigationManager, projectId, artifactType, ...rest })
      }
    );
  }),
  comparison: React3.memo(({ data, navigationManager, projectId, artifactType, ...rest }) => {
    const { t } = useAppTranslation3();
    return /* @__PURE__ */ jsx4(
      ArtifactThumbnailContainer,
      {
        title: t("productComparison"),
        subtitle: `Comparing ${Object.keys(data.products || {}).length} products`,
        onClick: () => navigationManager.navigate("artifact", { projectId, artifactType }),
        artifactContent: /* @__PURE__ */ jsx4(ProductComparison, { data, navigationManager, projectId, artifactType, ...rest })
      }
    );
  }),
  evaluation: React3.memo(({ data, navigationManager, projectId, artifactType, ...rest }) => {
    const { t } = useAppTranslation3();
    return /* @__PURE__ */ jsx4(
      ArtifactThumbnailContainer,
      {
        title: t("productEvaluation"),
        subtitle: `${data.executiveSummary?.product.name} - Rating: ${data.ratings?.overallRating || "N/A"}/5`,
        onClick: () => navigationManager.navigate("artifact", { projectId, artifactType }),
        artifactContent: /* @__PURE__ */ jsx4(ProductEvaluation, { data, navigationManager, projectId, artifactType, ...rest })
      }
    );
  }),
  fallback: React3.memo(({ data, navigationManager, projectId, artifactType, ...rest }) => {
    const { t } = useAppTranslation3();
    const name = data?.name;
    const reason = data?.reason;
    return /* @__PURE__ */ jsx4(
      ArtifactThumbnailContainer,
      {
        title: name || t("unknownArtifact"),
        subtitle: reason || t("noComponentRegistered"),
        onClick: () => navigationManager.navigate("artifact", { projectId, artifactType }),
        artifactContent: /* @__PURE__ */ jsx4(ArtifactFallback, { data, navigationManager, projectId, artifactType, ...rest })
      }
    );
  })
};
var Project = ({
  navigation,
  layout,
  projectName,
  renderMessages,
  renderArtifacts,
  renderArtifactInSplitPanel,
  maxService,
  renderContext,
  nextSteps,
  params,
  sendMessage,
  interruptProject,
  promptInputAction,
  logger,
  tags = []
}) => {
  const { t } = useAppTranslation3();
  const { deleteProject } = maxService;
  const isMessageInFlight = renderContext?.turnStatus === TurnStatus.Processing;
  const [isMetadataUpdate, setIsMetadataUpdate] = React3.useState(false);
  React3.useEffect(() => {
    if (!isMessageInFlight) {
      setIsMetadataUpdate(false);
    }
  }, [isMessageInFlight]);
  const shouldAutoScroll = isMessageInFlight && !isMetadataUpdate;
  const isFavorite = tags.includes("favourite");
  const handleToggleFavorite = React3.useCallback(() => {
    const newTags = isFavorite ? tags.filter((tag) => tag !== "favourite") : [...tags, "favourite"];
    setIsMetadataUpdate(true);
    sendMessage({ tags: newTags, contentBlocks: [] });
  }, [sendMessage, tags, isFavorite]);
  const [showRenameModal, setShowRenameModal] = React3.useState(false);
  const [renameValue, setRenameValue] = React3.useState("");
  const renameInputRef = React3.useRef(null);
  React3.useEffect(() => {
    if (showRenameModal && renameInputRef.current) {
      renameInputRef.current.focus();
    }
  }, [showRenameModal]);
  const handleRenameProject = React3.useCallback(() => {
    setRenameValue(projectName || "Untitled project");
    setShowRenameModal(true);
  }, [projectName]);
  const handleRenameSubmit = React3.useCallback(() => {
    const trimmedValue = renameValue.trim();
    if (trimmedValue.length > 0 && trimmedValue.length <= 50) {
      setIsMetadataUpdate(true);
      sendMessage({ projectName: trimmedValue, contentBlocks: [] });
      setShowRenameModal(false);
    }
  }, [renameValue, sendMessage]);
  const isRenameValid = renameValue.trim().length > 0 && renameValue.length <= 50;
  React3.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape")
        return;
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement || activeElement instanceof HTMLSelectElement) {
        return;
      }
      event.preventDefault();
      interruptProject();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [interruptProject]);
  const initialPrompt = navigation.consumeEphemeralState("project")?.initialPrompt;
  const autoScrollRef = React3.useRef(null);
  const stickyInputRef = React3.useRef(null);
  const promptInputRef = React3.useRef(null);
  const messagesContainerRef = React3.useRef(null);
  const [showTailButton, setShowTailButton] = React3.useState(false);
  const handleReportsClick = React3.useCallback(() => {
    if (layout.splitPanel.shown) {
      layout.splitPanel.hide();
    } else {
      const renderedArtifacts = renderArtifacts(THUMBNAIL_ARTIFACTS, ["search", "comparison", "evaluation"]);
      const artifactCount2 = Object.keys(renderedArtifacts).length;
      layout.splitPanel.updateMetadata({ size: 320 });
      layout.splitPanel.setContent({
        header: `Reports created (${artifactCount2})`,
        content: /* @__PURE__ */ jsx4(SpaceBetween2, { size: "l", children: Object.entries(renderedArtifacts).map(([artifactType, node]) => /* @__PURE__ */ jsx4(React3.Fragment, { children: node }, artifactType)) })
      });
    }
  }, [layout.splitPanel, renderArtifacts]);
  const handleDeleteProject = React3.useCallback(async () => {
    const projectId = params.projectId;
    if (!projectId)
      return;
    try {
      const result = await deleteProject(projectId, {
        onForceDeleteRequired: (performForceDelete) => {
          layout.modal.updateMetadata({ size: "medium" });
          layout.modal.setContent({
            header: t("deleteActiveProjects"),
            content: /* @__PURE__ */ jsx4(
              ForceDeleteModal2,
              {
                projects: [{ id: projectId, name: projectName || t("project") }],
                onConfirm: async () => {
                  await performForceDelete();
                  layout.modal.hide();
                  navigation.navigate("home", {});
                  layout.flashbar.addItem({
                    type: "success",
                    header: t("projectDeleted"),
                    content: `Successfully deleted ${projectName}`,
                    dismissible: true
                  });
                },
                onCancel: () => layout.modal.hide()
              }
            )
          });
        }
      });
      if (result.success !== false) {
        navigation.navigate("home", {});
        layout.flashbar.addItem({
          type: "success",
          header: t("projectDeleted"),
          content: `Successfully deleted ${projectName}`,
          dismissible: true
        });
      }
    } catch (err) {
      console.error("Failed to delete project:", err);
      const errorMessage = err instanceof Error ? err.message : t("failedToDeleteProject");
      layout.flashbar.addItem({
        type: "error",
        header: t("failedToDeleteProject"),
        content: errorMessage,
        dismissible: true
      });
    }
  }, [deleteProject, projectName, layout, navigation, params]);
  const hasSentInitialMessage = React3.useRef(false);
  React3.useEffect(() => {
    if (hasSentInitialMessage.current) {
      return;
    }
    if (initialPrompt) {
      sendMessage(initialPrompt);
      hasSentInitialMessage.current = true;
      return;
    }
  }, []);
  React3.useEffect(() => {
    promptInputRef.current?.focus();
  }, []);
  const SPLIT_PANEL_COMPONENTS = React3.useMemo(() => createSplitPanelArtifactComponents(), []);
  const [pendingArtifact, setPendingArtifact] = React3.useState(null);
  const events = React3.useMemo(
    () => ({
      onNewlyAvailableArtifact: (artifactType) => {
        if (layout.windowSize === "s") {
          return;
        }
        setPendingArtifact(artifactType);
      }
    }),
    [layout.windowSize]
  );
  React3.useEffect(() => {
    if (!pendingArtifact)
      return;
    const artifactType = pendingArtifact;
    const component = SPLIT_PANEL_COMPONENTS[artifactType] || SPLIT_PANEL_COMPONENTS.fallback;
    renderArtifactInSplitPanel({ [artifactType]: component });
    setPendingArtifact(null);
  }, [pendingArtifact]);
  const messages = React3.useMemo(() => renderMessages(MESSAGE_COMPONENTS, events), [renderMessages, events]);
  const artifactCount = React3.useMemo(() => {
    const renderedArtifacts = renderArtifacts(THUMBNAIL_ARTIFACTS, ["search", "comparison", "evaluation"]);
    const count = Object.keys(renderedArtifacts).length;
    return count;
  }, [renderArtifacts]);
  const renderedMessages = !messages ? initialPrompt ? /* @__PURE__ */ jsx4(Box, { padding: "xxl", textAlign: "center", children: /* @__PURE__ */ jsx4(Spinner, { size: "large" }) }) : /* @__PURE__ */ jsx4(Box, { padding: "xxl", textAlign: "center", color: "text-body-secondary", children: t("noMessagesYet") }) : messages;
  return /* @__PURE__ */ jsxs2(ContentLayout2, { children: [
    /* @__PURE__ */ jsx4(
      StickyHeader,
      {
        title: projectName || t("project"),
        layout,
        navigation,
        stickyId: "project-header",
        actions: /* @__PURE__ */ jsxs2(SpaceBetween2, { direction: "horizontal", size: "xs", children: [
          /* @__PURE__ */ jsx4(
            Button2,
            {
              variant: layout.windowSize === "l" && !layout.splitPanel.shown ? "link" : "icon",
              iconName: "transcript",
              onClick: handleReportsClick,
              ariaLabel: `Reports created${artifactCount !== void 0 ? ` (${artifactCount})` : ""}`,
              children: layout.windowSize === "l" && !layout.splitPanel.shown && `Reports created ${artifactCount !== void 0 ? `(${artifactCount})` : ""}`
            }
          ),
          /* @__PURE__ */ jsx4(
            Button2,
            {
              iconName: isFavorite ? "star-filled" : "star",
              variant: "normal",
              onClick: handleToggleFavorite,
              ariaLabel: isFavorite ? t("removeFromFavorites") : t("addToFavorites"),
              disabled: isMessageInFlight
            }
          ),
          /* @__PURE__ */ jsx4(
            ButtonDropdown,
            {
              variant: "icon",
              items: [
                { text: "Rename project", id: "rename" },
                { text: t("deleteProject"), id: "delete" }
              ],
              ariaLabel: t("projectActions"),
              expandToViewport: true,
              onItemClick: ({ detail }) => {
                if (detail.id === "delete") {
                  handleDeleteProject();
                } else if (detail.id === "rename") {
                  handleRenameProject();
                }
              }
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsxs2("div", { className: Project_module_default.pageWrapper, children: [
      /* @__PURE__ */ jsx4("div", { ref: messagesContainerRef, className: Project_module_default.messagesContainer, children: /* @__PURE__ */ jsx4(
        AutoScrollToAnchor,
        {
          ref: autoScrollRef,
          isActive: shouldAutoScroll,
          getViewportOffsets: () => ({
            top: layout.viewportYOffset,
            bottom: 100
            // Sticky input is in document flow, not fixed
          }),
          onAnchorVisibilityChange: ({ anchorInView }) => setShowTailButton(!anchorInView),
          children: /* @__PURE__ */ jsx4(ContentGrid2, { splitPanelOpen: layout.splitPanel.shown, children: renderedMessages })
        }
      ) }),
      /* @__PURE__ */ jsxs2("div", { ref: stickyInputRef, className: Project_module_default.stickyInput, children: [
        showTailButton && /* @__PURE__ */ jsx4("div", { className: Project_module_default.tailButtonContainer, children: /* @__PURE__ */ jsx4(
          Button2,
          {
            iconName: "angle-down",
            onClick: () => {
              setShowTailButton(false);
              autoScrollRef.current?.scrollToBottom();
            },
            ariaLabel: t("scrollToBottom"),
            children: "Bottom"
          }
        ) }),
        /* @__PURE__ */ jsx4("div", { className: Project_module_default.stickyInputGradient }),
        /* @__PURE__ */ jsx4(ContentGrid2, { splitPanelOpen: layout.splitPanel.shown, children: /* @__PURE__ */ jsxs2("div", { className: Project_module_default.stickyInputContent, children: [
          /* @__PURE__ */ jsx4(
            "div",
            {
              className: `${Project_module_default.nextStepsContainer} ${isMessageInFlight || !nextSteps || nextSteps.length === 0 ? Project_module_default.nextStepsHidden : ""}`,
              children: /* @__PURE__ */ jsxs2("div", { className: Project_module_default.nextStepsLayout, children: [
                /* @__PURE__ */ jsx4(Box, { variant: "span", color: "text-label", fontWeight: "bold", className: Project_module_default.nextStepsLabel, children: "Next" }),
                /* @__PURE__ */ jsx4("div", { className: Project_module_default.nextStepsCarousel, children: /* @__PURE__ */ jsx4(
                  Carousel,
                  {
                    items: (nextSteps || []).map((nextStep, index) => /* @__PURE__ */ jsx4(
                      Button2,
                      {
                        onClick: () => {
                          logger.biMetric({
                            metricName: "NextStepButtonClicked",
                            unit: "Count",
                            value: 1,
                            dimensions: {
                              ClickObj: "next-step-button"
                            }
                          });
                          sendMessage(nextStep);
                        },
                        children: nextStep
                      },
                      index
                    )),
                    flexibleItemWidths: true,
                    variant: "compact",
                    backgroundColor: colorBackgroundStatusInfo
                  }
                ) })
              ] })
            }
          ),
          /* @__PURE__ */ jsx4(
            PromptInput,
            {
              ref: promptInputRef,
              lines: 1,
              placeholderText: t("askAQuestion"),
              onSubmit: sendMessage,
              isDisabled: isMessageInFlight,
              promptInputAction
            }
          )
        ] }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx4(
      Modal,
      {
        visible: showRenameModal,
        onDismiss: () => setShowRenameModal(false),
        header: "Rename project",
        size: "medium",
        footer: /* @__PURE__ */ jsx4(Box, { float: "right", children: /* @__PURE__ */ jsxs2(SpaceBetween2, { direction: "horizontal", size: "xs", children: [
          /* @__PURE__ */ jsx4(Button2, { onClick: () => setShowRenameModal(false), disabled: isMessageInFlight, children: "Cancel" }),
          /* @__PURE__ */ jsx4(Button2, { variant: "primary", onClick: handleRenameSubmit, disabled: !isRenameValid || isMessageInFlight, loading: isMessageInFlight, children: "Save name" })
        ] }) }),
        children: /* @__PURE__ */ jsx4(
          FormField,
          {
            label: "Project name",
            constraintText: "Project name can have a maximum of 50 characters.",
            errorText: renameValue.trim().length === 0 ? "Project name cannot be empty." : renameValue.length > 50 ? "Project name is too long." : void 0,
            children: /* @__PURE__ */ jsx4(
              Input,
              {
                ref: renameInputRef,
                value: renameValue,
                onChange: ({ detail }) => setRenameValue(detail.value),
                onKeyDown: (event) => {
                  if (event.detail.key === "Enter") {
                    event.preventDefault();
                    handleRenameSubmit();
                  }
                },
                placeholder: "[Project name]",
                disabled: isMessageInFlight,
                ariaLabel: "Project name"
              }
            )
          }
        )
      }
    )
  ] });
};

// src/Artifact/ArtifactScreen.tsx
import * as React4 from "react";
import ContentLayout3 from "@cloudscape-design/components/content-layout";
import {
  ProductComparison as ProductComparison2,
  ProductComparisonAsStandaloneReport,
  ProductSearch as ProductSearch2,
  ProductEvaluation as ProductEvaluation2,
  ArtifactFallback as ArtifactFallback2,
  convertToCSV
} from "@amzn/awsmp-max-agentic-ui-artifacts";
import { StickyHeader as StickyHeader2 } from "@amzn/awsmp-max-ux-patterns/StickyHeader";
import { ContentGrid as ContentGrid3 } from "@amzn/awsmp-max-ux-patterns/ContentGrid";
import { ArtifactActions } from "@amzn/awsmp-max-ux-patterns/ArtifactActions";
import { exportAsPDF, exportAsCSV } from "@amzn/awsmp-max-ux-patterns/DocumentExporter";
import { useAppTranslation as useAppTranslation4 } from "@amzn/awsmp-max-translations";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var ArtifactScreen = ({ renderArtifacts, artifactType, artifactName }) => {
  const { t } = useAppTranslation4();
  const [dynamicTitle, setDynamicTitle] = React4.useState(null);
  const artifactComponentsWithName = React4.useMemo(() => {
    const displayName = dynamicTitle || artifactName || t("artifact");
    const stickyId = `artifact-header-${displayName.toLowerCase().replace(/\s+/g, "-")}`;
    return {
      search: (props) => /* @__PURE__ */ jsxs3(ContentLayout3, { children: [
        /* @__PURE__ */ jsx5(
          StickyHeader2,
          {
            title: displayName,
            layout: props.layoutManager,
            navigation: props.navigationManager,
            stickyId,
            actions: /* @__PURE__ */ jsx5(ArtifactActions, { logger: props.logger })
          }
        ),
        /* @__PURE__ */ jsx5(ContentGrid3, { children: /* @__PURE__ */ jsx5(ProductSearch2, { ...props, setTitle: setDynamicTitle, mode: "edit" }) })
      ] }),
      comparison: (props) => {
        const handleDownloadCSV = async () => {
          const filename = `${displayName.toLowerCase().replace(/\s+/g, "-")}.csv`;
          await exportAsCSV(convertToCSV(props.data, t), filename);
        };
        const handleDownloadPDF = async () => {
          const filename = `${displayName.toLowerCase().replace(/\s+/g, "-")}.pdf`;
          await exportAsPDF(/* @__PURE__ */ jsx5(ProductComparisonAsStandaloneReport, { data: props.data, title: displayName }), filename);
        };
        return /* @__PURE__ */ jsxs3(ContentLayout3, { children: [
          /* @__PURE__ */ jsx5(
            StickyHeader2,
            {
              title: displayName,
              layout: props.layoutManager,
              navigation: props.navigationManager,
              stickyId,
              actions: /* @__PURE__ */ jsx5(ArtifactActions, { onDownloadCSV: handleDownloadCSV, onDownloadPDF: handleDownloadPDF, logger: props.logger })
            }
          ),
          /* @__PURE__ */ jsx5(ContentGrid3, { children: /* @__PURE__ */ jsx5(ProductComparison2, { ...props }) })
        ] });
      },
      evaluation: (props) => {
        const handleDownloadPDF = async () => {
          const filename = `${displayName.toLowerCase().replace(/\s+/g, "-")}.pdf`;
          await exportAsPDF(/* @__PURE__ */ jsx5(ProductEvaluation2, { ...props }), filename);
        };
        return /* @__PURE__ */ jsxs3(ContentLayout3, { children: [
          /* @__PURE__ */ jsx5(
            StickyHeader2,
            {
              title: displayName,
              layout: props.layoutManager,
              navigation: props.navigationManager,
              stickyId,
              actions: /* @__PURE__ */ jsx5(ArtifactActions, { onDownloadPDF: handleDownloadPDF, logger: props.logger })
            }
          ),
          /* @__PURE__ */ jsx5(ContentGrid3, { children: /* @__PURE__ */ jsx5(ProductEvaluation2, { ...props }) })
        ] });
      },
      fallback: ArtifactFallback2
    };
  }, [artifactName, dynamicTitle, t]);
  const artifactContent = React4.useMemo(() => {
    const rendered = renderArtifacts(artifactComponentsWithName, [artifactType]);
    return rendered[artifactType] || null;
  }, [renderArtifacts, artifactType, artifactComponentsWithName]);
  return /* @__PURE__ */ jsx5(ContentLayout3, { children: artifactContent });
};

// src/Artifact/ArtifactWrapper.tsx
import Button3 from "@cloudscape-design/components/button";
import ButtonDropdown2 from "@cloudscape-design/components/button-dropdown";
import ContentLayout4 from "@cloudscape-design/components/content-layout";
import SpaceBetween3 from "@cloudscape-design/components/space-between";
import Icon3 from "@cloudscape-design/components/icon";
import * as React5 from "react";
import { StickyHeader as StickyHeader3 } from "@amzn/awsmp-max-ux-patterns/StickyHeader";

// src/Artifact/Artifact.module.css
var Artifact_default = {
  headerLayout: "Artifact_headerLayout",
  buttonDropdownContainer: "Artifact_buttonDropdownContainer",
  contentArea: "Artifact_contentArea",
  genAiIcon: "Artifact_genAiIcon"
};

// src/Artifact/ArtifactWrapper.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var ArtifactWrapper = ({
  artifactName,
  children,
  onDownloadCSV,
  onDownloadPDF,
  onExpand,
  layout,
  navigation,
  logger
}) => {
  const downloadItems = React5.useMemo(() => {
    const items = [];
    if (onDownloadPDF) {
      items.push({ text: "PDF", id: "pdf", disabled: false });
    }
    if (onDownloadCSV) {
      items.push({ text: "CSV", id: "csv", disabled: false });
    }
    return items;
  }, [onDownloadPDF, onDownloadCSV]);
  const handleDownloadClick = React5.useCallback(
    ({ detail }) => {
      if (detail.id === "pdf" && onDownloadPDF) {
        logger?.biMetric({
          metricName: "Click",
          value: 1,
          unit: "Count",
          dimensions: {
            ClickSelect: "download-pdf-button"
          }
        });
        onDownloadPDF();
      } else if (detail.id === "csv" && onDownloadCSV) {
        logger?.biMetric({
          metricName: "Click",
          value: 1,
          unit: "Count",
          dimensions: {
            ClickSelect: "download-csv-button"
          }
        });
        onDownloadCSV();
      }
    },
    [onDownloadPDF, onDownloadCSV, logger]
  );
  const hasActions = onExpand || downloadItems.length > 0;
  const renderActions = () => /* @__PURE__ */ jsxs4(SpaceBetween3, { direction: "horizontal", size: "s", children: [
    onExpand && /* @__PURE__ */ jsx6(
      Button3,
      {
        iconName: "expand",
        variant: "normal",
        onClick: () => {
          logger?.biMetric({
            metricName: "Click",
            value: 1,
            unit: "Count",
            dimensions: {
              ClickSelect: "fullscreen-button"
            }
          });
          onExpand();
        },
        children: "Fullscreen"
      }
    ),
    downloadItems.length > 0 && /* @__PURE__ */ jsxs4(ButtonDropdown2, { items: downloadItems, onItemClick: handleDownloadClick, children: [
      /* @__PURE__ */ jsx6(Icon3, { name: "download" }),
      " Download"
    ] })
  ] });
  if (!onExpand && layout && navigation) {
    return /* @__PURE__ */ jsxs4(ContentLayout4, { children: [
      /* @__PURE__ */ jsx6(
        StickyHeader3,
        {
          title: artifactName,
          layout,
          navigation,
          stickyId: `artifact-header-${artifactName.toLowerCase().replace(/\s+/g, "-")}`,
          actions: hasActions ? renderActions() : void 0
        }
      ),
      /* @__PURE__ */ jsx6("div", { className: Artifact_default.contentArea, children })
    ] });
  }
  return /* @__PURE__ */ jsxs4(ContentLayout4, { children: [
    hasActions && /* @__PURE__ */ jsx6("div", { style: { display: "flex", justifyContent: "flex-end", marginBottom: "16px" }, children: renderActions() }),
    /* @__PURE__ */ jsx6("div", { className: Artifact_default.contentArea, children })
  ] });
};

// src/max/Compare/Compare.tsx
import * as React7 from "react";
import Alert3 from "@cloudscape-design/components/alert";
import Box2 from "@cloudscape-design/components/box";
import ContentLayout5 from "@cloudscape-design/components/content-layout";
import SpaceBetween4 from "@cloudscape-design/components/space-between";
import TextContent from "@cloudscape-design/components/text-content";
import { useAppTranslation as useAppTranslation6 } from "@amzn/awsmp-max-translations";
import { ContentGrid as ContentGrid4 } from "@amzn/awsmp-max-ux-patterns/ContentGrid";
import { ProductSelector } from "@amzn/awsmp-max-ux-patterns/ProductSelector";
import { StickyHeader as StickyHeader4 } from "@amzn/awsmp-max-ux-patterns/StickyHeader";

// src/max/Compare/SampleProductComparisonSection.tsx
import * as React6 from "react";
import Spinner2 from "@cloudscape-design/components/spinner";
import Alert2 from "@cloudscape-design/components/alert";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import { useAppTranslation as useAppTranslation5 } from "@amzn/awsmp-max-translations";

// src/max/Compare/utils.ts
var formatProductNamesWithEllipsis = (productNames, maxTotalChars = 60) => {
  if (productNames.length === 0)
    return "";
  if (productNames.length === 1) {
    return productNames[0].length > maxTotalChars ? `${productNames[0].slice(0, maxTotalChars - 3)}...` : productNames[0];
  }
  const separator = " vs ";
  const separatorLength = separator.length;
  const totalSeparators = productNames.length - 1;
  const totalSeparatorChars = totalSeparators * separatorLength;
  const availableCharsForNames = maxTotalChars - totalSeparatorChars;
  if (availableCharsForNames <= 0) {
    return productNames.map((name) => `${name.slice(0, 1)}...`).join(separator);
  }
  const charsPerProduct = Math.floor(availableCharsForNames / productNames.length);
  const formattedNames = productNames.map((name) => {
    if (name.length <= charsPerProduct) {
      return name;
    }
    const truncateAt = Math.max(1, charsPerProduct - 3);
    return `${name.slice(0, truncateAt)}...`;
  });
  return formattedNames.join(separator);
};

// src/max/Compare/Compare.module.scss
var Compare_module_default = {
  constrainedContent: "Compare_module_constrainedContent",
  container: "Compare_module_container",
  tokenContainer: "Compare_module_tokenContainer",
  comparisonCard: "Compare_module_comparisonCard",
  productCard: "Compare_module_productCard",
  productIcon: "Compare_module_productIcon",
  productContent: "Compare_module_productContent",
  productName: "Compare_module_productName",
  productVendor: "Compare_module_productVendor",
  productId: "Compare_module_productId",
  productRemove: "Compare_module_productRemove",
  vsCircle: "Compare_module_vsCircle"
};

// src/max/Compare/SampleProductComparisonSection.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var productCache = /* @__PURE__ */ new Map();
var SampleProductComparisonSection = ({
  maxService,
  projectCreation,
  productIdPairs,
  onRemoveProduct: _onRemoveProduct
}) => {
  const { t } = useAppTranslation5();
  const [products, setProducts] = React6.useState(/* @__PURE__ */ new Map());
  const [isLoading, setIsLoading] = React6.useState(true);
  const [error, setError] = React6.useState(null);
  const [isCreatingProject, setIsCreatingProject] = React6.useState(false);
  const handleComparisonClick = React6.useCallback(
    (product1, product2) => {
      if (isCreatingProject)
        return;
      setIsCreatingProject(true);
      setError(null);
      try {
        const initialPrompt = `Compare ${product1.productName} vs ${product2.productName}`;
        const projectName = formatProductNamesWithEllipsis([product1.productName, product2.productName]);
        projectCreation.createProjectWithStreaming({
          projectName,
          projectDescription: `Comparing ${product1.productName} vs ${product2.productName}`,
          agent: {
            agentId: "comparison",
            listingIds: [product1.productId, product2.productId]
          },
          contentBlocks: [{ text: initialPrompt }]
        });
      } catch (err) {
        console.error("Failed to create comparison project:", err);
        setError(err instanceof Error ? err.message : t("failedToCreateComparisonProject"));
        setIsCreatingProject(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectCreation, isCreatingProject]
  );
  React6.useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const productMap = /* @__PURE__ */ new Map();
        const allProductIds = /* @__PURE__ */ new Set();
        productIdPairs.forEach(([id1, id2]) => {
          allProductIds.add(id1);
          allProductIds.add(id2);
        });
        const BATCH_SIZE = 10;
        for (const productId of allProductIds) {
          if (productCache.has(productId)) {
            productMap.set(productId, productCache.get(productId));
          }
        }
        const productIdsToFetch = Array.from(allProductIds).filter((id) => !productCache.has(id));
        for (let i = 0; i < productIdsToFetch.length; i += BATCH_SIZE) {
          const chunk = productIdsToFetch.slice(i, i + BATCH_SIZE);
          try {
            const result = await maxService.getListingOverview(chunk);
            if (result.success && result.output && typeof result.output === "object" && "output" in result.output) {
              const typedOutput = result.output;
              const results = typedOutput.output.getListingOverview?.results || [];
              for (let idx = 0; idx < results.length; idx++) {
                const resultItem = results[idx];
                const productId = chunk[idx];
                if (resultItem.status === "success" && resultItem.listingView) {
                  const listingData = JSON.parse(resultItem.listingView);
                  const listing = listingData.data?.listing;
                  const overview = listing?.listingDetail?.overview;
                  if (listing && overview) {
                    const product = {
                      productId: listing.id,
                      productName: overview.listingName || t("unknownProduct"),
                      vendorName: overview.creator?.creatorName || t("unknownVendor"),
                      productLogoUrl: overview.listingThumbnailUrl
                    };
                    productMap.set(productId, product);
                    productCache.set(productId, product);
                  } else {
                    productMap.set(productId, {
                      productId,
                      productName: t("productNotFound"),
                      vendorName: t("unknownVendor")
                    });
                  }
                } else {
                  const errorMessage = resultItem.error?.message || t("unknownError");
                  console.error(`Product not found ${productId}:`, errorMessage);
                  productMap.set(productId, {
                    productId,
                    productName: t("productNotFound"),
                    vendorName: t("unknownVendor")
                  });
                }
              }
            } else {
              for (const productId of chunk) {
                productMap.set(productId, {
                  productId,
                  productName: t("productNotFound"),
                  vendorName: t("unknownVendor")
                });
              }
            }
          } catch (err) {
            console.error(`Failed to fetch product batch:`, err);
            for (const productId of chunk) {
              productMap.set(productId, {
                productId,
                productName: t("productNotFound"),
                vendorName: t("unknownVendor")
              });
            }
          }
        }
        setProducts(productMap);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError(err instanceof Error ? err.message : t("failedToLoadProducts"));
      } finally {
        setIsLoading(false);
      }
    };
    if (productIdPairs.length > 0) {
      fetchProducts();
    } else {
      setIsLoading(false);
    }
  }, [maxService, productIdPairs]);
  if (isLoading) {
    return /* @__PURE__ */ jsx7("div", { style: { display: "flex", justifyContent: "center", padding: "40px 0" }, children: /* @__PURE__ */ jsx7(Spinner2, { size: "large" }) });
  }
  if (error) {
    return /* @__PURE__ */ jsx7(Alert2, { type: "error", dismissible: true, onDismiss: () => setError(null), children: error });
  }
  if (productIdPairs.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx7(ColumnLayout, { columns: 2, borders: "none", children: productIdPairs.map(([productId1, productId2], index) => {
    const product1 = products.get(productId1);
    const product2 = products.get(productId2);
    if (!product1 || !product2) {
      return null;
    }
    return /* @__PURE__ */ jsxs5(
      "button",
      {
        className: Compare_module_default.comparisonCard,
        onClick: () => handleComparisonClick(product1, product2),
        disabled: isCreatingProject,
        "aria-label": `Compare ${product1.productName} vs ${product2.productName}`,
        type: "button",
        children: [
          /* @__PURE__ */ jsxs5("div", { className: Compare_module_default.productCard, children: [
            product1.productLogoUrl && /* @__PURE__ */ jsx7("img", { src: product1.productLogoUrl, alt: "", className: Compare_module_default.productIcon }),
            /* @__PURE__ */ jsx7("div", { className: Compare_module_default.productContent, children: /* @__PURE__ */ jsx7("div", { className: Compare_module_default.productName, children: product1.productName }) })
          ] }),
          /* @__PURE__ */ jsx7("div", { className: Compare_module_default.vsCircle, children: "vs" }),
          /* @__PURE__ */ jsxs5("div", { className: Compare_module_default.productCard, children: [
            product2.productLogoUrl && /* @__PURE__ */ jsx7("img", { src: product2.productLogoUrl, alt: "", className: Compare_module_default.productIcon }),
            /* @__PURE__ */ jsx7("div", { className: Compare_module_default.productContent, children: /* @__PURE__ */ jsx7("div", { className: Compare_module_default.productName, children: product2.productName }) })
          ] })
        ]
      },
      `comparison-${index}`
    );
  }) });
};

// src/max/Compare/Compare.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var Compare = ({ maxService, projectCreation, navigation, layout }) => {
  const { t } = useAppTranslation6();
  const [error, setError] = React7.useState(null);
  const [selectedProducts, setSelectedProducts] = React7.useState([]);
  const handleConfirm = React7.useCallback(
    (products) => {
      setError(null);
      try {
        const listingIds = products.map((p) => p.productId);
        const productNames = formatProductNamesWithEllipsis(products.map((p) => p.productName));
        const productList = products.map((p) => p.productName).join(", ");
        const comparisonPrompt = `Compare these AWS Marketplace products: ${productList}.`;
        projectCreation.createProjectWithStreaming({
          projectName: productNames,
          projectDescription: `Comparing ${products.length} products`,
          agent: {
            agentId: "comparison",
            listingIds
          },
          contentBlocks: [
            {
              text: comparisonPrompt
            }
          ]
        });
      } catch (err) {
        console.error("Failed to start comparison:", err);
        setError(err instanceof Error ? err.message : t("failedToStartComparison"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectCreation]
  );
  return /* @__PURE__ */ jsxs6(ContentLayout5, { children: [
    /* @__PURE__ */ jsx8(StickyHeader4, { title: t("compareProducts"), layout, navigation, stickyId: "compare-header" }),
    /* @__PURE__ */ jsx8(ContentGrid4, { children: /* @__PURE__ */ jsxs6(SpaceBetween4, { size: "l", children: [
      error && /* @__PURE__ */ jsx8(Alert3, { type: "error", dismissible: true, onDismiss: () => setError(null), children: error }),
      /* @__PURE__ */ jsx8(Box2, { children: /* @__PURE__ */ jsx8("div", { className: Compare_module_default.container, children: /* @__PURE__ */ jsxs6(SpaceBetween4, { size: "m", children: [
        /* @__PURE__ */ jsx8(TextContent, { children: /* @__PURE__ */ jsx8("p", { children: t("whichProductsToCompare") }) }),
        /* @__PURE__ */ jsx8(ProductSelector, { maxService, onChange: setSelectedProducts, onConfirm: handleConfirm }),
        selectedProducts.length === 0 && /* @__PURE__ */ jsxs6(Fragment2, { children: [
          /* @__PURE__ */ jsx8(TextContent, { children: /* @__PURE__ */ jsx8("p", { children: /* @__PURE__ */ jsx8("strong", { children: t("popularComparisons") }) }) }),
          /* @__PURE__ */ jsx8(
            SampleProductComparisonSection,
            {
              maxService,
              projectCreation,
              productIdPairs: [
                ["prodview-wtyi5lgtce6n6", "prodview-xof6cmsunnnra"],
                // Databricks Data Intelligence Platform vs Dataiku for Enterprise AI - Overall AWS MP best seller
                ["prodview-qjlabherxghtq", "prodview-d5kxpjxyrsdkw"],
                // Brave Search API Pro vs Exa - Bestselling API-based agents
                ["prodview-nlyysa5n2s7h6", "prodview-35pip7pt7ilbi"],
                // Wiz MCP Server vs Fortinet Lacework FortiCNAPP - Bestselling MCP server
                ["prodview-3xw4sjqv2pb22", "prodview-5ophamrbfxt44"]
                // Drata Security & Compliance Automation Platform vs Vanta - Confirmed with Grimmes and Krishna
              ]
            }
          )
        ] })
      ] }) }) })
    ] }) })
  ] });
};

// src/max/Research/Research.tsx
import * as React8 from "react";
import ContentLayout6 from "@cloudscape-design/components/content-layout";
import SpaceBetween5 from "@cloudscape-design/components/space-between";
import Box3 from "@cloudscape-design/components/box";
import FormField2 from "@cloudscape-design/components/form-field";
import TextContent2 from "@cloudscape-design/components/text-content";
import Link2 from "@cloudscape-design/components/link";
import FileTokenGroup from "@cloudscape-design/components/file-token-group";
import Button4 from "@cloudscape-design/components/button";
import Spinner3 from "@cloudscape-design/components/spinner";
import { StickyHeader as StickyHeader5 } from "@amzn/awsmp-max-ux-patterns/StickyHeader";
import { ContentGrid as ContentGrid5 } from "@amzn/awsmp-max-ux-patterns/ContentGrid";
import { ProductSearch as ProductSearch3 } from "@amzn/awsmp-max-ux-patterns/ProductSearch";
import { ProductToken } from "@amzn/awsmp-max-ux-patterns/ProductToken";
import { PromptInput as PromptInput2 } from "@amzn/awsmp-max-ux-patterns/PromptInput";
import { createEvaluationProposalPrompt } from "@amzn/awsmp-max-model";
import { useAppTranslation as useAppTranslation7 } from "@amzn/awsmp-max-translations";

// src/max/Research/Research.module.scss
var Research_module_default = {
  container: "Research_module_container",
  tokenContainer: "Research_module_tokenContainer",
  pageWrapper: "Research_module_pageWrapper",
  contentContainer: "Research_module_contentContainer",
  stickyInput: "Research_module_stickyInput",
  stickyInputGradient: "Research_module_stickyInputGradient",
  stickyInputContent: "Research_module_stickyInputContent"
};

// src/max/Research/Research.tsx
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var Research = ({ maxService, projectCreation, navigation, layout, logger }) => {
  const { t } = useAppTranslation7();
  const [selectedProduct, setSelectedProduct] = React8.useState(null);
  const [files, setFiles] = React8.useState([]);
  const [isUploading, setIsUploading] = React8.useState(false);
  const [isProcessing, setIsProcessing] = React8.useState(false);
  const [uploadCallbacks] = React8.useState({
    onProgress: (_fileName, _percent) => {
    },
    onProcessing: () => {
      setIsUploading(false);
      setIsProcessing(true);
    },
    onComplete: () => {
      setIsProcessing(false);
    }
  });
  const fileInputRef = React8.useRef(null);
  const handleProductSelect = React8.useCallback((product) => {
    setSelectedProduct(product);
  }, []);
  const handleUploadFilesClick = React8.useCallback(() => {
    logger.biMetric({
      metricName: "ResearchUploadFilesLinkClicked",
      unit: "Count",
      value: 1,
      dimensions: {
        ClickAct: "Click",
        ClickSelect: "UploadFiles",
        ClickObj: "Link",
        ClickFeat: "Research"
      }
    });
    fileInputRef.current?.click();
  }, [logger]);
  const handleFileChange = React8.useCallback((event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      setFiles(Array.from(selectedFiles));
    }
  }, []);
  const handleFileRemove = React8.useCallback((fileIndex) => {
    setFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  }, []);
  const handleAnswerQuestions = React8.useCallback(() => {
    if (!selectedProduct)
      return;
    logger.biMetric({
      metricName: "ResearchAnswerQuestionsLinkClicked",
      unit: "Count",
      value: 1,
      dimensions: {
        ClickAct: "Click",
        ClickSelect: "AnswerQuestions",
        ClickObj: "Link",
        ClickFeat: "Research"
      }
    });
    const hasFiles = files.length > 0;
    const fileNames = files.map((f) => f.name).join(", ");
    if (hasFiles) {
      setIsUploading(true);
    }
    projectCreation.createProjectWithStreaming({
      projectName: `Proposal: ${selectedProduct.productName}`,
      projectDescription: `Creating tailored proposal for ${selectedProduct.productName}`,
      agent: {
        agentId: "evaluation",
        listingId: selectedProduct.productId
      },
      contentBlocks: createEvaluationProposalPrompt({
        productName: selectedProduct.productName,
        vendorName: selectedProduct.vendorName,
        productId: selectedProduct.productId,
        hasFiles,
        fileCount: files.length,
        fileNames
      }),
      files,
      uploadCallbacks
    });
  }, [selectedProduct, files, uploadCallbacks, projectCreation, logger]);
  const handleSubmitPrompt = React8.useCallback(
    ({
      contentBlocks,
      files: submittedFiles,
      uploadCallbacks: submittedCallbacks
    }) => {
      if (!selectedProduct)
        return;
      const userText = contentBlocks?.[0] && "text" in contentBlocks[0] ? contentBlocks[0].text : "";
      if (!userText) {
        handleAnswerQuestions();
        return;
      }
      const filesToUpload = submittedFiles || files;
      projectCreation.createProjectWithStreaming({
        projectName: `Proposal: ${selectedProduct.productName}`,
        projectDescription: `Creating tailored proposal for ${selectedProduct.productName}`,
        agent: {
          agentId: "evaluation",
          listingId: selectedProduct.productId
        },
        contentBlocks: [
          { text: userText },
          // User's description
          ...createEvaluationProposalPrompt({
            productName: selectedProduct.productName,
            vendorName: selectedProduct.vendorName,
            productId: selectedProduct.productId,
            hasFiles: filesToUpload.length > 0,
            fileCount: filesToUpload.length,
            fileNames: filesToUpload.map((f) => f.name).join(", ")
          })
        ],
        files: filesToUpload,
        uploadCallbacks: submittedCallbacks || uploadCallbacks
      });
    },
    [selectedProduct, files, uploadCallbacks, projectCreation, handleAnswerQuestions]
  );
  return /* @__PURE__ */ jsxs7(ContentLayout6, { children: [
    /* @__PURE__ */ jsx9(StickyHeader5, { title: t("researchProducts"), layout, navigation, stickyId: "research-header" }),
    /* @__PURE__ */ jsxs7("div", { className: Research_module_default.pageWrapper, children: [
      /* @__PURE__ */ jsx9("div", { className: Research_module_default.contentContainer, children: /* @__PURE__ */ jsx9(ContentGrid5, { children: /* @__PURE__ */ jsx9(SpaceBetween5, { size: "l", children: /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "m", children: [
        /* @__PURE__ */ jsx9(Box3, { children: /* @__PURE__ */ jsxs7("div", { className: Research_module_default.container, children: [
          !selectedProduct && /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "s", children: [
            /* @__PURE__ */ jsx9(TextContent2, { children: /* @__PURE__ */ jsx9("p", { children: t("whichProductForProposal") }) }),
            /* @__PURE__ */ jsx9(FormField2, { label: t("product"), children: /* @__PURE__ */ jsx9(
              ProductSearch3,
              {
                maxService,
                onSelect: handleProductSelect,
                placeholder: t("searchAwsMarketplaceProducts"),
                disabled: false
              }
            ) })
          ] }),
          selectedProduct && /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "m", children: [
            /* @__PURE__ */ jsx9(TextContent2, { children: /* @__PURE__ */ jsx9(Box3, { variant: "span", color: "text-body-secondary", children: t("selectedProduct") }) }),
            /* @__PURE__ */ jsx9("div", { className: Research_module_default.tokenContainer, children: /* @__PURE__ */ jsx9(ProductToken, { product: selectedProduct, onDismiss: () => setSelectedProduct(null) }) })
          ] })
        ] }) }),
        selectedProduct && /* @__PURE__ */ jsx9(Box3, { children: /* @__PURE__ */ jsxs7("div", { className: Research_module_default.container, children: [
          /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "m", children: [
            /* @__PURE__ */ jsx9(TextContent2, { children: /* @__PURE__ */ jsxs7("p", { children: [
              "To create a proposal for ",
              /* @__PURE__ */ jsx9("strong", { children: selectedProduct.productName }),
              " tailored to your organization, tell me about your requirements. You can",
              " ",
              isUploading || isProcessing ? /* @__PURE__ */ jsx9(Box3, { variant: "span", color: "text-body-secondary", children: t("uploadFiles") }) : /* @__PURE__ */ jsx9(Link2, { onFollow: handleUploadFilesClick, children: t("uploadFiles") }),
              " ",
              "or",
              " ",
              isUploading || isProcessing ? /* @__PURE__ */ jsx9(Box3, { variant: "span", color: "text-body-secondary", children: t("answerAFewQuestions") }) : /* @__PURE__ */ jsx9(Link2, { onFollow: handleAnswerQuestions, children: t("answerAFewQuestions") }),
              "."
            ] }) }),
            files.length > 0 && /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "m", children: [
              /* @__PURE__ */ jsx9(
                FileTokenGroup,
                {
                  items: files.map((file) => ({
                    file
                  })),
                  onDismiss: isUploading || isProcessing ? () => {
                  } : ({ detail }) => handleFileRemove(detail.fileIndex),
                  alignment: "horizontal",
                  showFileSize: true,
                  showFileLastModified: true,
                  showFileThumbnail: true,
                  i18nStrings: {
                    removeFileAriaLabel: () => t("removeFile"),
                    limitShowFewer: t("showFewerFiles"),
                    limitShowMore: t("showMoreFiles"),
                    errorIconAriaLabel: t("errorIconAriaLabel"),
                    warningIconAriaLabel: t("warningIconAriaLabel")
                  }
                }
              ),
              /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "xs", direction: "horizontal", alignItems: "center", children: [
                (isUploading || isProcessing) && /* @__PURE__ */ jsxs7(Fragment3, { children: [
                  /* @__PURE__ */ jsx9(Spinner3, { size: "normal" }),
                  /* @__PURE__ */ jsx9(Box3, { children: isUploading ? t("uploadingFiles") : t("scanningFiles") })
                ] }),
                /* @__PURE__ */ jsx9(Box3, { float: "right", children: /* @__PURE__ */ jsx9(
                  Button4,
                  {
                    variant: "primary",
                    onClick: handleAnswerQuestions,
                    disabled: isUploading || isProcessing,
                    loading: isUploading || isProcessing,
                    children: t("continueWithFiles", { count: files.length, fileWord: files.length === 1 ? t("file") : t("files") })
                  }
                ) })
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx9(
            "input",
            {
              ref: fileInputRef,
              type: "file",
              multiple: true,
              accept: ".pdf,.doc,.docx,.txt",
              onChange: handleFileChange,
              style: { display: "none" }
            }
          )
        ] }) })
      ] }) }) }) }),
      selectedProduct && /* @__PURE__ */ jsxs7("div", { className: Research_module_default.stickyInput, children: [
        /* @__PURE__ */ jsx9("div", { className: Research_module_default.stickyInputGradient }),
        /* @__PURE__ */ jsx9(ContentGrid5, { children: /* @__PURE__ */ jsx9("div", { className: Research_module_default.stickyInputContent, children: /* @__PURE__ */ jsx9(
          PromptInput2,
          {
            lines: 1,
            placeholderText: t("describeYourNeeds"),
            onSubmit: handleSubmitPrompt,
            isDisabled: isUploading || isProcessing
          }
        ) }) })
      ] })
    ] })
  ] });
};
export {
  ArtifactScreen as Artifact,
  ArtifactWrapper,
  Compare,
  Home,
  Project,
  Research
};
//# sourceMappingURL=index.js.map
